import { useEffect, useState } from "react";
import { Mic, MicOff, Loader2, X as XIcon, Sparkles, CheckCircle2, RefreshCw, Volume2 } from "lucide-react";
import Modal from "./Modal";
import HidroFeedbackBar from "./HidroFeedbackBar";
import useVoiceCapture from "../lib/useVoiceCapture";
import { uploadUrl } from "../lib/uploads";

/**
 * VoiceTextarea — Wave 19 #107.
 *
 * Drop-in replacement for <textarea> that adds a mic button. Recording →
 * Whisper transcribe → Claude refine → opens a 2-pane review modal so
 * the rep can pick raw vs refined (or edit either) before committing.
 *
 * On commit, calls `onCommit({ refined, raw, extracted })`. Parents that
 * just want a single string can use `onChange(text)` and ignore the rest.
 *
 * Props mirror a normal textarea (`value`, `onChange`, `placeholder`,
 * `rows`, `className`, `disabled`, `data-testid`).
 *
 * Extra props:
 *   context           — refine context hint, e.g. "meeting_outcome"
 *   onCommit          — ({ refined, raw, extracted }) => void. When set,
 *                       the parent stores the raw alongside the refined.
 *   testIdPrefix      — namespaces the mic / modal test ids
 */
export default function VoiceTextarea({
  value,
  onChange,
  placeholder,
  rows = 4,
  className = "",
  disabled = false,
  context = "lead_activity",
  onCommit,
  testIdPrefix = "voice",
  baseClass = "hv-input w-full",
  wrapperClassName = "",
  inputRef,
  ...rest
}) {
  const cap = useVoiceCapture({ context });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [refined, setRefined] = useState("");

  // When transcription finishes, open the review modal
  useEffect(() => {
    if (cap.result) {
      setRaw(cap.result.raw || "");
      setRefined(cap.result.refined || cap.result.raw || "");
      setReviewOpen(true);
    }
  }, [cap.result]);

  // Iter 371 — Also open the review modal as soon as the audio upload
  // resolves (usually a second after stop, before Whisper), so the
  // user hears the playback while transcription is still churning.
  useEffect(() => {
    if (cap.audioUpload && !cap.result) {
      setReviewOpen(true);
    }
  }, [cap.audioUpload, cap.result]);

  const useRefined = () => {
    const text = refined.trim();
    if (onCommit) {
      onCommit({
        refined: text,
        raw,
        extracted: cap.result?.extracted || {},
        voice_upload_id: cap.result?.voice_upload_id || cap.audioUpload?.id || null,
        voice_url: cap.result?.voice_url || cap.audioUpload?.url || null,
        voice_mime: cap.result?.voice_mime || cap.audioUpload?.mime || null,
        duration_sec: cap.result?.duration_sec || cap.audioUpload?.duration_sec || null,
      });
    } else {
      onChange?.(text);
    }
    setReviewOpen(false);
    cap.setResult(null);
    cap.setAudioUpload?.(null);
  };
  const useRawOnly = () => {
    if (onCommit) {
      onCommit({
        refined: raw.trim(),
        raw,
        extracted: {},
        voice_upload_id: cap.result?.voice_upload_id || cap.audioUpload?.id || null,
        voice_url: cap.result?.voice_url || cap.audioUpload?.url || null,
        voice_mime: cap.result?.voice_mime || cap.audioUpload?.mime || null,
        duration_sec: cap.result?.duration_sec || cap.audioUpload?.duration_sec || null,
      });
    } else {
      onChange?.(raw.trim());
    }
    setReviewOpen(false);
    cap.setResult(null);
    cap.setAudioUpload?.(null);
  };

  const cancelReview = () => {
    setReviewOpen(false);
    cap.setResult(null);
    cap.setAudioUpload?.(null);
  };

  const fmtTime = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = (s || 0) % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  return (
    <>
      <div className={`relative ${wrapperClassName}`}>
        <textarea
          ref={inputRef}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled || cap.recording || cap.uploading}
          className={`${baseClass} pr-10 ${className}`}
          {...rest}
        />
        {/* Mic button — bottom-right inside the textarea */}
        <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 print:hidden">
          {cap.recording && (
            <span
              className="text-[10px] font-mono text-rose-300 px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/30"
              data-testid={`${testIdPrefix}-elapsed`}
            >
              {fmtTime(cap.elapsedSec)}
            </span>
          )}
          {cap.uploading ? (
            <button
              type="button"
              disabled
              className="w-7 h-7 rounded-full flex items-center justify-center bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1]"
              title="Transcribing & refining…"
              data-testid={`${testIdPrefix}-uploading`}
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            </button>
          ) : cap.recording ? (
            <button
              type="button"
              onClick={cap.stop}
              className="w-7 h-7 rounded-full flex items-center justify-center bg-rose-500/20 border border-rose-500/50 text-rose-300 hover:bg-rose-500/30 transition-colors"
              title="Stop recording"
              data-testid={`${testIdPrefix}-stop`}
              style={{
                boxShadow: cap.level > 0
                  ? `0 0 ${4 + cap.level * 14}px rgba(244,63,94,${0.3 + cap.level * 0.4})`
                  : "none",
              }}
            >
              <MicOff className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={cap.start}
              disabled={disabled}
              className="w-7 h-7 rounded-full flex items-center justify-center bg-white/5 border border-white/15 text-[#a0aabf] hover:bg-[#A78BFA]/15 hover:border-[#A78BFA]/40 hover:text-[#C4B5FD] transition-colors"
              title="Dictate this field — Hidro will transcribe and clean up"
              data-testid={`${testIdPrefix}-start`}
            >
              <Mic className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {cap.error && (
        <div className="mt-1 text-[11px] text-rose-300" data-testid={`${testIdPrefix}-error`}>
          {cap.error}
        </div>
      )}

      {reviewOpen && (
        <Modal
          open
          onClose={cancelReview}
          title="Voice note review"
          testId={`${testIdPrefix}-review-modal`}
          maxWidth="max-w-7xl"
          footer={
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button
                type="button"
                onClick={cancelReview}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid={`${testIdPrefix}-review-cancel`}
              >
                <XIcon className="w-3 h-3" /> Discard
              </button>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={useRawOnly}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid={`${testIdPrefix}-review-use-raw`}
                  title="Skip the AI cleanup — use the verbatim transcript"
                >
                  Use raw
                </button>
                <button
                  type="button"
                  onClick={useRefined}
                  disabled={!refined.trim()}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid={`${testIdPrefix}-review-use-refined`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Use refined
                </button>
              </div>
            </div>
          }
        >
          {/* Iter 371 — Playable voice note. Renders as soon as the raw
              audio uploads, i.e. usually before the transcript arrives. */}
          {cap.audioUpload?.url && (
            <div className="mb-3 hv-card p-3 flex items-center gap-3" data-testid={`${testIdPrefix}-review-player`}>
              <Volume2 className="w-4 h-4 text-[#A78BFA] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
                  Voice note · {fmtTime(cap.audioUpload.duration_sec || cap.result?.duration_sec || 0)}
                </div>
                <audio
                  controls
                  src={uploadUrl(cap.audioUpload.id)}
                  preload="metadata"
                  className="w-full h-8"
                  data-testid={`${testIdPrefix}-review-audio`}
                >
                  Your browser does not support audio playback.
                </audio>
              </div>
              {!cap.result && (
                <span className="text-[10px] text-[#A78BFA] font-medium inline-flex items-center gap-1 shrink-0">
                  <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* RAW */}
            <div className="border border-white/10 rounded-md overflow-hidden bg-black/20 flex flex-col">
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#647087] border-b border-white/10 flex items-center gap-1.5 shrink-0">
                <Mic className="w-3 h-3" /> Raw transcript
                <span className="ml-auto text-[10px] normal-case tracking-normal text-[#475063]">
                  edit if a name or place was mis-heard
                </span>
              </div>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={22}
                placeholder={!cap.result ? "Waiting for transcription…" : ""}
                className="hv-input w-full text-sm leading-relaxed border-0 rounded-none bg-transparent focus:ring-0 flex-1 resize-y min-h-[300px] md:min-h-[60vh] font-normal"
                data-testid={`${testIdPrefix}-review-raw-text`}
              />
            </div>
            {/* REFINED */}
            <div className="border border-[#A78BFA]/30 rounded-md overflow-hidden bg-[#A78BFA]/[0.04] flex flex-col">
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#C4B5FD] border-b border-[#A78BFA]/30 flex items-center gap-1.5 shrink-0">
                <Sparkles className="w-3 h-3" /> Hidro-refined
                {cap.result?.extracted && Object.keys(cap.result.extracted).length > 0 && (
                  <span
                    className="ml-auto text-[10px] normal-case tracking-normal text-[#1FB6C1]"
                    title={JSON.stringify(cap.result.extracted)}
                  >
                    +{Object.keys(cap.result.extracted).length} field{Object.keys(cap.result.extracted).length === 1 ? "" : "s"} auto-filled
                  </span>
                )}
              </div>
              <textarea
                value={refined}
                onChange={(e) => setRefined(e.target.value)}
                rows={22}
                className="hv-input w-full text-sm leading-relaxed border-0 rounded-none bg-transparent focus:ring-0 flex-1 resize-y min-h-[300px] md:min-h-[60vh] font-normal"
                data-testid={`${testIdPrefix}-review-refined-text`}
                placeholder={!refined ? (cap.result ? "AI refine unavailable — edit the raw transcript on the left or click 'Use raw'." : "Waiting for transcription…") : ""}
              />
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-[#647087]">
              Both versions are saved — the refined text becomes the body, the
              raw transcript is kept as metadata so nothing is lost.
              {cap.result?.duration_sec ? ` · Recorded ${fmtTime(cap.result.duration_sec)}` : ""}
            </div>
            {/* §16 — feedback on Hidro's refinement quality */}
            <HidroFeedbackBar
              surface={`voice_refine_${context}`}
              surfaceRefId={cap.result?.id || null}
              aiOutput={cap.result?.refined || ""}
              shippedGetter={() => refined}
              compact
              testId="voice-refine-feedback"
            />
          </div>
        </Modal>
      )}
    </>
  );
}
